<template>
  <div class="site-header-container">
    <header class="site-header">
      <div class="site-header__container">
        <!-- Left -->
        <div class="site-header__left">
          <component
            :is="titleElement"
          >
            <a
              class="site-header__logo"
              :href="$variable('routes.root')"
            >
              <span
                class="visually-hidden"
                v-text="$string('general.site_header.back_to_homepage')"
              />

              <brand-logo />
            </a>
          </component>
        </div>

        <nav class="site-header__navigation">
          <navigation
            :nav-link-colors="navLinkColors"
            :scribble-exclusions="scribbleExclusions"
            :desktop="desktop"
            :signposts="signposts"
            :highlights="highlights"
          />
        </nav>

        <!-- Right -->
        <div
          class="site-header__right"
          :style="`--wisp-count-color: ${liquid.wispCountColor}`"
        >
          <!-- Dynamic Icons -->
          <template
            v-for="block of liquid.blocks"
            :key="block.id"
          >
            <button
              v-if="block.type === 'info' && liquid.wispEnabled &&
                ((isMobile && block.settings.mobile_enabled) ||
                  (!isMobile && block.settings.desktop_enabled))"
              :style="`--wisp-count-color: ${liquid.wispCountColor}`"
              class="wisp site-header__notifications icon-button custom-icon"
              type="button"
            >
              <span
                class="visually-hidden"
                v-text="$string('general.site_header.notifications')"
              />
              <span
                class="visually-hidden"
                v-text="$string('general.site_header.custom_icon')"
              />

              <responsive-image
                v-if="block.settings.custom_icon_image"
                :image="{
                  src: block.settings.custom_icon_image,
                  alt: 'Custom Info Icon',
                  width: 24,
                  height: 24
                }"
                class="site-header__icon-image"
              />
              <icon-info v-else />
            </button>

            <!-- Search icon -->

            <a
              v-else-if="block.type === 'search' &&
                ((isMobile && block.settings.mobile_enabled) ||
                  (!isMobile && block.settings.desktop_enabled))"
              class="site-header__search icon-button custom-icon"
              href="#"
              @click.prevent="openSearchOverlay"
            >
              <span
                class="visually-hidden"
                v-text="$string('general.site_header.search')"
              />

              <responsive-image
                v-if="block.settings.custom_icon_image"
                :image="{
                  src: block.settings.custom_icon_image,
                  alt: 'Custom Search Icon',
                  width: 24,
                  height: 24
                }"
                class="site-header__icon-image"
              />
              <!-- <icon-search /> -->
            </a>

            <!-- Account icon -->

            <template
              v-else-if="block.type === 'account' &&
                ((isMobile && block.settings.mobile_enabled) ||
                  (!isMobile && block.settings.desktop_enabled))"
            >
              <!-- Acount icon when user logged in but mobile view  -->
              <template v-if="liquid.accountName">
                <button
                  v-if="isMobile"
                  class="site-header__account"
                  :class="{ 'site-header__account--no-name':
                    !block.settings.show_user_name_mobile
                  }"
                  type="button"
                  @click.prevent="handleMobileAccountToggle"
                >
                  <span
                    class="visually-hidden"
                    v-text="$string('general.site_header.account')"
                  />

                  <responsive-image
                    v-if="block.settings.custom_icon_image_logged_in"
                    v-show="getActiveOverlay?.namespace != 'accountDrawer'"
                    :image="{
                      src: block.settings.custom_icon_image_logged_in,
                      alt: 'Custom Account Icon (Logged In)',
                      width: 24,
                      height: 24
                    }"
                    class="site-header__icon-image custom-account-icon"
                  />

                  <icon-account
                    v-else
                    v-show="getActiveOverlay?.namespace != 'accountDrawer'"
                    class="icon-account-logged"
                  />

                  <icon-close
                    v-show="getActiveOverlay?.namespace === 'accountDrawer'"
                    class="icon-close"
                  />

                  <span
                    v-if="liquid.accountName && block.settings.show_user_name_mobile"
                    class="site-header__account-name"
                    v-text="liquid.accountName"
                  />
                </button>

                <!-- account icon when user logged in but desktop view -->
                <template v-else>
                  <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events -->
                  <a
                    class="site-header__account"
                    :href="$variable('routes.account.url')"
                    @hover="handleDesktopAccountToggle"
                    @mouseover="handleDesktopAccountToggle"
                  >
                    <span
                      class="visually-hidden"
                      v-text="$string('general.site_header.account')"
                    />

                    <responsive-image
                      v-if="block.settings.custom_icon_image_logged_in"
                      :image="{
                        src: block.settings.custom_icon_image_logged_in,
                        alt: 'Custom Account Icon (Logged In)',
                        width: 24,
                        height: 24
                      }"
                      class="site-header__icon-image custom-account-icon"
                    />

                    <icon-account
                      v-else
                      v-show="getActiveOverlay?.namespace != 'accountDrawer'"
                      class="icon-account-logged"
                    />

                    <icon-close
                      v-show="getActiveOverlay?.namespace === 'accountDrawer'"
                    />

                    <span
                      v-if="liquid.accountName"
                      class="site-header__account-name"
                      v-text="liquid.accountName"
                    />
                  </a>

                  <btn
                    :aria-expanded="accountNavActive"
                    :aria-label="$string('general.site_header.toggle_account')"
                    class="accessibility__sign-post"
                    :label="false"
                    modifiers="text"
                    show-icon
                    type="button"
                    @focusin="handleDesktopAccountToggle"
                    @keydown.enter.prevent="accountNavActive = !accountNavActive"
                    @keydown.space.prevent="accountNavActive = !accountNavActive"
                  />
                </template>
              </template>

              <!-- Account icon when user not logged in -->
              <a
                v-else
                class="site-header__account custom-icon-not-logged"
                :href="$variable('routes.account.url')"
              >
                <span
                  class="visually-hidden"
                  v-text="$string('general.site_header.account')"
                />

                <responsive-image
                  v-if="block.settings.custom_icon_image_logged_out"
                  :image="{
                    src: block.settings.custom_icon_image_logged_out,
                    alt: 'Custom Account Icon (Logged Out)',
                    width: 24,
                    height: 24
                  }"
                  class="site-header__icon-image"
                />
                <icon-account v-else />

              </a>

              <account-nav
                v-show="accountNavActive"
                :navigation="account"
                @hover="handleDesktopAccountToggle"
                @focusin="handleDesktopAccountToggle"
                @mouseover="handleDesktopAccountToggle"
                @mouseout="handleDesktopAccountToggle"
                @focusout="handleDesktopAccountToggle"
              />
            </template>

            <!-- Cart icon -->

            <a
              v-else-if="block.type === 'cart' &&
                ((isMobile && block.settings.mobile_enabled) ||
                  (!isMobile && block.settings.desktop_enabled))"
              class="site-header__cart icon-button"
              :class="{'not-empty': cart.item_count,
                       'custom-cart-icon': cart.item_count === 0
              }"
              :href="$variable('routes.cart.url')"
            >
              <span
                class="visually-hidden"
                v-text="$string('general.site_header.cart')"
              />

              <responsive-image
                v-if="cart.item_count > 0 && block.settings.custom_icon_image_non_empty"
                :image="{
                  src: block.settings.custom_icon_image_non_empty,
                  alt: 'Custom Cart Icon (Non-Empty)',
                  width: 24,
                  height: 24
                }"
                class="site-header__icon-image custom-cart-icon-non-empty"
              />

              <!-- Custom Image for Empty Cart -->

              <responsive-image
                v-else-if="cart.item_count === 0 && block.settings.custom_icon_image_empty"
                :image="{
                  src: block.settings.custom_icon_image_empty,
                  alt: 'Custom Cart Icon (Empty)',
                  width: 24,
                  height: 24
                }"
                class="site-header__icon-image"
              />

              <icon-cart v-else />
              <span
                v-show="cart.item_count"
                class="site-header__cart-badge critical-clear text-other-label-s"
                aria-hidden="true"
                v-text="cart.item_count"
              />
              <span
                class="visually-hidden"
                v-text="$string(
                  'general.site_header.cart_item_count',
                  {
                    replace: { count: cart.item_count },
                    pluralise: cart.item_count,
                  }
                )"
              />
            </a>

            <a
              v-else-if="block.type === 'custom-icon' &&
                ((isMobile && block.settings.mobile_enabled) ||
                  (!isMobile && block.settings.desktop_enabled))"
              :aria-label="$string('general.site_header.custom_icon')"
              class="site-header__icon icon-button custom-icon"
              :href="block.settings.icon_link || '#'"
              rel="noopener noreferrer"
              target="_blank"
            >

              <responsive-image
                :image="{
                  src: block.settings.icon_image,
                  alt: block.settings.icon_alt,
                  width: 24,
                  height: 24
                }"
                class="site-header__icon-image"
              />
            </a>
          </template>

          <!--
            Wisepops notifications toggle - "wisp" class required
            https://support.wisepops.com/article/wnwnl8wgh2-add-feed-embedded-or-floating
          -->

          <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events -->

          <btn
            :aria-expanded="accountNavActive"
            :aria-label="$string('general.site_header.toggle_account')"
            class="accessibility__sign-post"
            :label="false"
            modifiers="text"
            show-icon
            type="button"
            @focusin="handleDesktopAccountToggle"
            @keydown.enter.prevent="accountNavActive = !accountNavActive"
            @keydown.space.prevent="accountNavActive = !accountNavActive"
          />

          <account-nav
            v-show="accountNavActive"
            :navigation="account"
            @hover="handleDesktopAccountToggle"
            @focusin="handleDesktopAccountToggle"
            @mouseover="handleDesktopAccountToggle"
            @mouseout="handleDesktopAccountToggle"
            @focusout="handleDesktopAccountToggle"
          />

          <button
            v-if="mobile.handle"
            ref="menuDrawerToggle"
            class="site-header__menu icon-button"
            type="button"
            @click.prevent="handleMenuToggle"
          >
            <span
              class="visually-hidden"
              v-text="menuDrawerButtonName"
            />

            <icon-menu
              v-show="!isMenuDrawerOpen"
            />

            <icon-close
              v-show="isMenuDrawerOpen"
            />
          </button>
        </div>
      </div>
    </header>
    <overlay
      v-if="getActiveOverlay?.namespace === 'mainSearchOverlay'"
      key="mainSearchOverlay"
      block-class="main-search-overlay main-search-overlay--full-width"
      direction="down"
      namespace="mainSearchOverlay"
      type="dropdown"
      :show-close="false"
      :template="false"
    >
      <template #body>
        <!-- Search Bar Section -->
        <div class="search-bar">
          <div class="search-bar__form">
            <form
              class="search-bar__wrapper"
            >
              <icon-search />
              <label
                class="visually-hidden"
                for="search-bar"
                v-text="$string('search.submit')"
              />
              <input
                id="search-bar"
                v-model="query"
                class="search-bar__input"
                name="q"
                required
                type="search"
                :placeholder="$string('search.new_submit')"
                @input="handleSearchInput(query)"
                @focus="openSearchOverlay"
                @keydown.enter.prevent="redirectToSearchPage"
              >
              <button
                type="button"
                class="search-bar__close"
                @click="closeSearchOverlay"
              >
                <icon-close />
              </button>
            </form>
            <search-dropdown
              v-if="query"
              :show="!!query"
              :show-btn-discount="showBtnDiscount"
              :products="searchResults?.products || []"
              :collections="searchSuggestions?.collections || []"
              :pages="searchSuggestions?.pages || []"
              :blogs="searchSuggestions?.blogs || []"
              :query="query"
            />
          </div>
        </div>
      </template>
    </overlay>

    <!-- Menu drawer -->
    <overlay
      key="menuDrawer"
      block-class="menu-drawer-overlay"
      direction="left"
      namespace="menuDrawer"
      :show-close="false"
      type="drawer"
    >
      <template #title>
        <span
          v-text="$string('general.menu_drawer.title')"
        />
      </template>

      <template #body="overlay">
        <menu-drawer
          v-if="getComponentVisible('menu-drawer')"
          v-cloak
          :toggle="$refs.menuDrawerToggle"
          :nav-link-colors="navLinkColors"
          :nav-links-under="navLinksUnder"
          :handle="mobile.handle"
          :item-count="mobile.itemCount"
          :mobile-menus="mobile.mobileMenus"
          :mobile-signposts="mobileSignposts"
          :overlay="overlay"
          :signposts="signposts"
          :highlights="highlights"
          :ctas="[
            { title: liquid.ctaTitle1, link: liquid.ctaLink1 },
            { title: liquid.ctaTitle2, link: liquid.ctaLink2 },
            { title: liquid.ctaTitle3, link: liquid.ctaLink3 },
            { title: liquid.ctaTitle4, link: liquid.ctaLink4 },
            { title: liquid.ctaTitle5, link: liquid.ctaLink5 }
          ]"
        />

        <div
          v-if="!getComponentActive('menu-drawer')"
          class="navigation__menu-drawer"
        >
          <div
            v-for="(_, index) of [...Array(mobile.itemCount)]"
            :key="`menu-drawer-loading-${index}`"
            class="navigation__menu-drawer-link"
          >
            <span class="navigation__menu-drawer-loading" />
          </div>
        </div>
      </template>
    </overlay>

    <!-- Menu drawer (Account) -->
    <overlay
      v-if="account.handle"
      key="accountDrawer"
      block-class="account-drawer-overlay"
      direction="left"
      namespace="accountDrawer"
      type="drawer"
    >
      <template #body>
        <account-nav
          :navigation="account"
        />
      </template>
    </overlay>

    <overlay
      key="cartDrawerMobile"
      block-class="cart-drawer-mobile-overlay"
      direction="left"
      namespace="cartDrawerMobile"
      type="drawer"
    >
      <template #body="overlay">
        <cart-drawer-mobile
          v-if="getComponentVisible('cart-drawer-mobile')"
          v-cloak
          :overlay="overlay"
          :free-gift="freeGift"
          :promotion-message="promotionMessage"
        />
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Site header (site-header)
 * -----------------------------------------------------------------------------
 * Site header central banner containing logo, icon buttons, and menu drawer.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} liquid - Liquid section ID, used for theme editor events.
 * @param {Object} desktop - Desktop linklist, rendered as mega navs.
 * @param {Object} mobile - Mobile linklist handle and item count.
 * @param {Array} navLinkColors - Links that have been assigned colors.
 * @param {Array} navLinksUnder - Tier 2 links that should display under title
 * @param {Array} scribbleExclusions - Mega navs that shouldn't have scribbles.
 * @param {Array} highlights - Highlights section blocks.
 * @param {Array} signposts - Signposts section blocks.
 * @param {String} titleElement - HTML element of site logo based on page.
 * @param {Object} freeGift - Free gift settings.
 *@@ import Navigation from '../navigation/navigation.vue'
 */
import { defineAsyncComponent } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import Overlay from '~global/overlay/overlay'
import IconClose from '~icons/directional-navigation/close.svg'
import IconAccount from '~icons/general/account.svg'
import BrandLogo from '~icons/general/brand-logo.svg'
import IconCart from '~icons/general/cart.svg'
import IconInfo from '~icons/general/info.svg'
import IconMenu from '~icons/general/menu.svg'
import IconSearch from '~icons/general/search.svg'
import AccountNav from '../account-nav/account-nav.vue'
import Navigation from '../navigation/navigation.vue'
import ResponsiveImage from '~global/images/responsive-image'
import SearchDropdown from '../search-dropdown/search-dropdown.vue'
import { formatProduct } from '~/format/format-tagalys'
import { initTagalysSearchSuggestions, onTagalysReady } from '~/apis/tagalys'

export default {
  name: 'SiteHeader',

  components: {
    AccountNav,
    BrandLogo,
    Btn,
    IconAccount,
    IconCart,
    IconClose,
    IconInfo,
    IconMenu,
    IconSearch,
    SearchDropdown,
    ResponsiveImage,
    MenuDrawer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.menu-drawer' */
        '~async/menu-drawer/menu-drawer'
      ),
    }),
    CartDrawerMobile: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.cart-drawer-mobile' */
        '~async/cart-drawer/cart-drawer-mobile'
      ),
    }),
    Navigation,
    Overlay,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        accountName: '',
        sectionId: '',
        wispCountColor: '#A92B2B',
        wispEnabled: true,
        blocks: [],
      }),
    },
    account: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },
    desktop: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },
    mobile: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        itemCount: 6,
      }),
    },
    navLinkColors: {
      type: Array,
      default: () => [],
    },
    navLinksUnder: {
      type: Array,
      default: () => [],
    },
    scribbleExclusions: {
      type: Array,
      default: () => [],
    },
    signposts: {
      type: Array,
      default: () => [],
    },
    highlights: {
      type: Array,
      default: () => [],
    },
    freeGift: {
      type: Object,
      default: () => ({}),
    },
    titleElement: {
      type: String,
      default: 'h1',
      validator: (value) => {
        return ['h1', 'span'].includes(value)
      },
    },
    promotionMessage: {
      type: Object,
      default: () => ({}),
    },
    mobileSignposts: {
      type: Array,
      default: () => [],
    },
    showBtnDiscount: {
      type: String,
      default: 'show',
    },
  },

  data() {
    return {
      accountNavActive: false,
      showSearchOverlay: false,
      query: '',
      searchResults: {
        products: [],
      },
      searchSuggestions: {
        collections: [],
        pages: [],
        blogs: [],
      },
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute if the mobile menu drawer is open.
     * @returns {Boolean}
     */
    isMenuDrawerOpen() {
      return this.getActiveOverlay?.namespace === 'menuDrawer'
    },

    /**
     * Compute mobile menu drawer button's accessible name.
     * @returns {String}
     */
    menuDrawerButtonName() {
      return this.isMenuDrawerOpen
        ? this.$string('general.site_header.menu.close')
        : this.$string('general.site_header.menu.open')
    },
  },

  mounted() {
    onTagalysReady(() => {
      initTagalysSearchSuggestions(this.handleSuggestionsSuccess, this.handleSuggestionsFailure)
    })
  },
  methods: {
    handleSuggestionsSuccess(response) {
      const allItems = [
        ...response?.queries?.[0]?.items || [],
        ...response?.queries?.[1]?.items || [],
      ]
      this.searchSuggestions = {
        products: response.products || [],
        collections: allItems.filter(item => item.link && !item.link.includes('/blogs/') && !item.link.includes('/pages/')),
        pages: allItems.filter(item => item.link && item.link.includes('/pages/')),
        blogs: allItems.filter(item => item.link && item.link.includes('/blogs/')),
      }
    },
    handleSuggestionsFailure() {
      this.searchResults = { products: [], collections: [], pages: [] }
    },
    fetchSearchResults(query) {
      this.searchResults = null
      onTagalysReady(() => {
        window.Tagalys.APIConnector.SearchResults.new({
          params: {
            query,
            page: 1,
            perPage: 10,
            unavailableProducts: 'HIDE',
            includeCollections: true,
          },
          onSuccess: (response) => {
            this.searchResults = {
              products: response.products.map(product => formatProduct(product)),
              collections: response.collections || [],
              pages: response.filters.page || [],
            }
          },
          onFailure: () => {
            this.searchResults = { products: [], collections: [], pages: [] }
          },
        }).helpers.call()
      })
    },

    redirectToSearchPage() {
      if (this.query && this.query.trim() !== '') {
        const searchUrl = this.$variable('routes.search') || '/search'
        window.location.href = `${searchUrl}?q=${encodeURIComponent(this.query)}`
      }
    },

    handleIconClick(type, settings) {
      switch (type) {
        case 'account': {
          this.handleDesktopAccountToggle()
          break
        }
        case 'cart': {
          this.handleCartToggle()
          break
        }
        case 'search': {
          const searchUrl = this.$variable('routes.search') || '/search'
          window.location.href = searchUrl
          break
        }
        case 'custom-icon': {
          if (settings.icon_link) {
            window.location.href = settings.icon_link
          }
          break
        }
        default:
          throw new Error(`Unhandled icon type: ${type}`)
      }

    },
    openSearchOverlay() {
      this.openOverlay({
        component: 'main-search',
        ignoreDismissed: true,
        namespace: 'mainSearchOverlay',
      })
    },
    closeSearchOverlay() {
      this.closeOverlay('mainSearchOverlay')
    },

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Handle menu toggle.
     */
    handleMenuToggle() {
      this.toggleOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })
    },

    /**
     * Handle mobile account menu toggle.
     */
    handleMobileAccountToggle() {
      if (!this.isMobile) {
        return
      }
      // this.accountNavActive = !this.accountNavActive

      // Hidden this overlay as per clients request
      // this.toggleOverlay({
      //   component: 'account-drawer',
      //   ignoreDismissed: true,
      //   namespace: 'accountDrawer',
      // })

      const accountUrl = this.$variable('routes.account.url')
      window.location.href = accountUrl ? accountUrl : '/account'

    },

    /**
     * Handle account nav toggle events.
     * @param {Object} event - Focus or hover event.
     */
    handleDesktopAccountToggle(event) {
      if (this.isMobile) {
        return
      }

      if (event.type === 'hover' ||
        event.type === 'mouseover'
      ) {
        this.accountNavActive = true
        return
      }

      if (event.type === 'mouseout') {
        this.accountNavActive = false
        return
      }

      if (event.type === 'focusout' &&
        !event.relatedTarget.classList.contains('account-nav__link')
      ) {
        this.accountNavActive = false
      }
    },

    /**
     * Handle cart toggle.
     * - Prevents opening cart drawer on cart page.
     * - opens a different cart drawer on mobile.
     * @param {Object} event - Click event.
     */
    handleCartToggle(event) {
      if (
        this.$variable('page.type') === 'cart' ||
        !this.getComponentExists('cart-drawer')
      ) {
        return
      }

      event.preventDefault()

      if (this.isMobile) {
        this.toggleOverlay({
          component: 'cart-drawer-mobile',
          ignoreDismissed: true,
          namespace: 'cartDrawerMobile',
        })

        return
      }

      this.toggleOverlay({
        component: 'cart-drawer',
        ignoreDismissed: true,
        namespace: 'cartDrawer',
      })
    },

    /**
    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      if (!this.isMobile) {
        return
      }

      this.openOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })
    },
    handleSearchInput(query) {
      const upperValue = query?.toUpperCase()
      this.query = upperValue
      if (window.searchSuggestions) {
        window.searchSuggestions.helpers.updateQuery(query)
      }
      if (upperValue && upperValue.trim() !== '') {
        if (!this.showSearchOverlay) {
          this.openSearchOverlay()
        }
        this.fetchSearchResults(upperValue)
      } else {
        this.searchResults = null
        if (this.showSearchOverlay) {
          this.closeSearchOverlay()
        }
      }
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.closeOverlay('menuDrawer')
    },

    getIconComponent(type) {
      switch (type) {
        case 'account':
          return IconAccount
        case 'cart':
          return IconCart
        case 'search':
          return IconSearch
        case 'info':
          return IconInfo
        default:
          return null
      }
    },
  },
}
</script>
